import { useEffect, useMemo, useState } from 'react';
import { hrApi } from '@/api/hr.api';
import { SearchableSelect } from '@/components/ui/SearchableSelect';
import type { SelectOption } from '@/components/ui/Select';
import type { HrOvertimeType } from '@/types/hr';
import { showApiError } from '@/utils/apiError';
import { useSelectLabels } from '@/hooks/useSelectLabels';
import { useTranslation } from 'react-i18next';

export function OvertimeTypeSelect({
  label,
  value = '',
  onChange,
  placeholder,
  className,
  required,
  autoSelectWhenRequired = false,
}: {
  label?: string;
  value?: string;
  onChange?: (value: string) => void;
  placeholder?: string;
  className?: string;
  required?: boolean;
  autoSelectWhenRequired?: boolean;
}) {
  const selectLabels = useSelectLabels();
  const { t } = useTranslation('hr');
  const resolvedLabel = label ?? t('labels.overtimeType');
  const [types, setTypes] = useState<HrOvertimeType[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    hrApi
      .listOvertimeTypes()
      .then(setTypes)
      .catch((err) => showApiError(err, t('errors.failedToLoadOvertimeTypes')))
      .finally(() => setLoading(false));
  }, []);

  const options: SelectOption[] = useMemo(
    () =>
      types
        .filter((type) => type.active !== false)
        .map((type) => ({
          value: type.code,
          label: `${type.name} (${type.code})`,
        })),
    [types],
  );

  useEffect(() => {
    if (!autoSelectWhenRequired || !required || value || options.length === 0 || !onChange) {
      return;
    }
    onChange(options[0].value);
  }, [autoSelectWhenRequired, onChange, options, required, value]);

  return (
    <SearchableSelect
      className={className}
      label={resolvedLabel}
      required={required}
      value={value}
      onChange={onChange}
      options={options}
      placeholder={placeholder ?? selectLabels.selectOvertimeType}
      loading={loading}
      searchPlaceholder={selectLabels.searchOvertimeTypes}
      emptyMessage={selectLabels.noOvertimeTypes}
    />
  );
}
